import { NgTemplateOutlet } from "@angular/common"
import { ApplicationRef, Component, ContentChild, ElementRef, EmbeddedViewRef, Injector, TemplateRef } from "@angular/core"
import { CorpPopMenuService } from "./corp-pop-menu.service"

@Component({
    selector: 'corp-pop-menu',
    standalone: true,
    imports: [NgTemplateOutlet],
    template: `
    <ng-container *ngTemplateOutlet="triggerTpl"></ng-container>
  `
})
export class CorpPopMenuComponent {
    @ContentChild('trigger', { static: true }) triggerTpl!: TemplateRef<any>
    @ContentChild('content', { static: true }) contentTpl!: TemplateRef<any>

    private contentViewRef?: EmbeddedViewRef<any>
    private contentEl?: HTMLElement
    private menuRootEl?: HTMLElement
    private triggerEl?: HTMLElement

    private isOpen = false
    private skipNextOutsideClick = false

    private readonly edgeMargin = 8
    private readonly triggerGap = 6
    private readonly minMenuHeight = 160

    constructor(
        private appRef: ApplicationRef,
        private injector: Injector,
        private hostEl: ElementRef,
        private corpPopMenuService: CorpPopMenuService
    ) { }

    ngAfterViewInit() {
        const button = this.hostEl.nativeElement.querySelector('[content-pop-trigger]')
        if (button) {
            button.addEventListener('click', (ev: MouseEvent) => this.toggle(ev))
        }
    }

    toggle(event: MouseEvent) {
        if (this.isOpen) {
            this.close()
        } else {
            this.open(event)
        }
    }

    open(event: MouseEvent) {
        this.corpPopMenuService.register(this)
        this.isOpen = true

        this.triggerEl = (event.currentTarget as HTMLElement) ?? (event.target as HTMLElement)

        this.contentViewRef = this.contentTpl.createEmbeddedView({})
        this.contentViewRef.rootNodes.forEach(node => {
            if (node instanceof HTMLElement) {
                node.classList.add('corp-pop-menu-border')
            }
        })

        this.appRef.attachView(this.contentViewRef)
        this.contentViewRef.detectChanges()

        const container = document.createElement('div')
        container.style.position = 'fixed'
        container.style.zIndex = '9999'
        container.style.top = '0'
        container.style.left = '0'
        container.style.maxWidth = 'calc(100vw - 16px)'
        container.style.visibility = 'hidden'
        container.style.pointerEvents = 'none'

        document.body.appendChild(container)

        const menuRoot = this.contentViewRef.rootNodes.find(
            n => n.nodeType === Node.ELEMENT_NODE
        ) as HTMLElement
        container.appendChild(menuRoot)

        this.contentEl = container
        this.menuRootEl = menuRoot

        requestAnimationFrame(() => {
            this.reposition()
            if (!this.contentEl) {
                return
            }

            this.contentEl.style.visibility = 'visible'
            this.contentEl.style.pointerEvents = 'auto'
            this.contentEl.classList.add('corp-pop-menu-animate-fade-in')
        })

        this.skipNextOutsideClick = true
        document.addEventListener('click', this.outsideClick)
        window.addEventListener('resize', this.onViewportChange)
        window.addEventListener('scroll', this.onViewportChange, true)
    }

    private getScrollableElement(): HTMLElement | undefined {
        if (!this.menuRootEl) {
            return undefined
        }

        const explicitScrollable = this.menuRootEl.querySelector('[data-pop-scroll]') as HTMLElement | null
        if (explicitScrollable) {
            return explicitScrollable
        }

        const cssScrollable = this.menuRootEl.querySelector('.overflow-y-auto, .overflow-auto') as HTMLElement | null
        if (cssScrollable) {
            return cssScrollable
        }

        return this.menuRootEl
    }

    private reposition() {
        if (!this.contentEl || !this.menuRootEl || !this.triggerEl) {
            return
        }

        const triggerRect = this.triggerEl.getBoundingClientRect()
        const menuWidth = this.contentEl.offsetWidth
        const menuHeight = this.contentEl.offsetHeight

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        const spaceBelow = viewportHeight - triggerRect.bottom - this.edgeMargin
        const spaceAbove = triggerRect.top - this.edgeMargin

        const openBelow = spaceBelow >= menuHeight || spaceBelow >= spaceAbove

        let top = openBelow
            ? triggerRect.bottom + this.triggerGap
            : triggerRect.top - menuHeight - this.triggerGap

        top = Math.max(this.edgeMargin, Math.min(top, viewportHeight - menuHeight - this.edgeMargin))

        let left = triggerRect.left
        if (left + menuWidth + this.edgeMargin > viewportWidth) {
            left = triggerRect.right - menuWidth
        }
        left = Math.max(this.edgeMargin, Math.min(left, viewportWidth - menuWidth - this.edgeMargin))

        const availableHeight = openBelow
            ? viewportHeight - top - this.edgeMargin
            : triggerRect.top - this.edgeMargin - this.triggerGap

        const constrainedHeight = Math.max(this.minMenuHeight, Math.floor(availableHeight))
        const scrollableEl = this.getScrollableElement()

        if (scrollableEl) {
            if (scrollableEl !== this.menuRootEl) {
                this.menuRootEl.style.maxHeight = 'none'
                this.menuRootEl.style.overflowY = 'visible'
            }

            const nextMaxHeight = `${constrainedHeight}px`
            if (scrollableEl.style.maxHeight !== nextMaxHeight) {
                scrollableEl.style.maxHeight = nextMaxHeight
            }
            if (scrollableEl.style.overflowY !== 'auto') {
                scrollableEl.style.overflowY = 'auto'
            }
        }

        this.contentEl.style.left = `${left}px`
        this.contentEl.style.top = `${top}px`
    }

    private onViewportChange = (event?: Event) => {
        if (!this.isOpen) {
            return
        }

        // Ignore le scroll interne du pop-menu (évite le redimensionnement progressif)
        const target = event?.target as Node | null
        if (target && this.contentEl?.contains(target)) {
            return
        }

        this.reposition()
    }

    private outsideClick = (ev: MouseEvent) => {
        if (this.skipNextOutsideClick) {
            this.skipNextOutsideClick = false
            return
        }

        if ((ev.target as HTMLElement).closest('corp-pop-menu')) {
            return
        }

        if (this.contentEl && !this.contentEl.contains(ev.target as Node)) {
            this.close()
        }
    }

    close() {
        this.isOpen = false

        if (this.contentViewRef) {
            this.appRef.detachView(this.contentViewRef)
            this.contentViewRef.destroy()
            this.contentViewRef = undefined
        }

        if (this.contentEl) {
            this.contentEl.remove()
            this.contentEl = undefined
        }

        this.menuRootEl = undefined
        this.triggerEl = undefined

        document.removeEventListener('click', this.outsideClick)
        window.removeEventListener('resize', this.onViewportChange)
        window.removeEventListener('scroll', this.onViewportChange, true)

        this.corpPopMenuService.unregister(this)
    }

    ngOnDestroy() {
        this.close()
    }
}
